import { Fragment, useState } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { clsx } from 'clsx'

import { Button, ChevronDownIcon, DropdownMenu } from '@buffer-mono/popcorn'

import styles from './ResponsiveSegmentedControl.module.css'

export interface SegmentedOption {
  value: string
  label: string
  icon?: ReactNode
  /** Optional supporting copy shown beneath the label in the dropdown/drawer. */
  description?: string
}

export interface SegmentedGroup {
  /** Identifies the group; renders a divider before its options (not shown). */
  label: string
  options: readonly SegmentedOption[]
}

export type SegmentedEntry = SegmentedOption | SegmentedGroup

const isGroup = (entry: SegmentedEntry): entry is SegmentedGroup =>
  'options' in entry

/** Flattens groups so the trigger can resolve the currently-selected option. */
function findOption(
  options: readonly SegmentedEntry[],
  value: string,
): SegmentedOption | undefined {
  for (const entry of options) {
    if (isGroup(entry)) {
      const match = entry.options.find((option) => option.value === value)
      if (match) return match
    } else if (entry.value === value) {
      return entry
    }
  }
  return undefined
}

function OptionItem({ option }: { option: SegmentedOption }): JSX.Element {
  return (
    <DropdownMenu.RadioItem value={option.value}>
      <span className={styles.option}>
        {option.icon ? (
          <span className={styles.optionIcon} aria-hidden="true">
            {option.icon}
          </span>
        ) : null}
        <span className={styles.optionText}>
          <span className={styles.optionLabel}>{option.label}</span>
          {option.description ? (
            <span className={styles.optionDescription}>
              {option.description}
            </span>
          ) : null}
        </span>
      </span>
    </DropdownMenu.RadioItem>
  )
}

interface SegmentedDropdownProps {
  value: string
  onChange: (value: string) => void
  options: readonly SegmentedEntry[]
  className?: string
}

/**
 * The collapsed form on its own — a Popcorn `DropdownMenu` (neutral trigger
 * showing the selected icon + label) whose options may be flat or grouped, and
 * that carry an optional description shown beneath each label. `DropdownMenu`
 * renders as a popover on desktop and automatically as a bottom drawer on small
 * screens, so the collapsed control stays reachable on mobile. Used inside
 * ResponsiveSegmentedControl, and directly where a control needs to render its
 * collapsed dropdown in a different place from its expanded form.
 */
export function SegmentedDropdown({
  value,
  onChange,
  options,
  className,
}: SegmentedDropdownProps): JSX.Element {
  const selected = findOption(options, value)
  // Controlled so a selection can close the menu explicitly: in DropdownMenu's
  // drawer mode (small screens) Radix's select-to-close is a no-op, so picking a
  // value would otherwise leave the drawer open.
  const [open, setOpen] = useState(false)

  const handleChange = (next: string): void => {
    onChange(next)
    setOpen(false)
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger>
        <Button
          variant="secondary"
          size="medium"
          className={clsx(styles.trigger, className)}
        >
          <span className={styles.triggerLabel}>
            {selected?.icon ? (
              <span className={styles.optionIcon} aria-hidden="true">
                {selected.icon}
              </span>
            ) : null}
            <span className={styles.triggerText}>{selected?.label}</span>
          </span>
          <ChevronDownIcon className={styles.triggerChevron} aria-hidden />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start">
        <DropdownMenu.RadioGroup value={value} onChange={handleChange}>
          {options.map((entry) =>
            isGroup(entry) ? (
              // A single RadioGroup owns selection across every option, so groups
              // are separated by a divider rather than by nesting — nesting would
              // split selection state across groups.
              <Fragment key={entry.label}>
                <DropdownMenu.Separator />
                {entry.options.map((option) => (
                  <OptionItem key={option.value} option={option} />
                ))}
              </Fragment>
            ) : (
              <OptionItem key={entry.value} option={entry} />
            ),
          )}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

interface ResponsiveSegmentedControlProps {
  /** Current value — drives the collapsed dropdown. */
  value: string
  /** Called with the newly-selected value from the collapsed dropdown. */
  onChange: (value: string) => void
  /** Flat options, or groups (rendered with a heading), for the collapsed dropdown. */
  options: readonly SegmentedEntry[]
  /** The expanded control (e.g. a ToggleGroup) shown at wide widths. */
  children: ReactNode
}

/**
 * Shows the expanded segmented control (`children`) when its section card is
 * wide and collapses it to an equivalent Popcorn `DropdownMenu` on narrow cards,
 * so segmented controls stop overflowing section-card headers. Both stay mounted;
 * a container query against the SectionCard inline-size host swaps which is shown.
 */
export function ResponsiveSegmentedControl({
  value,
  onChange,
  options,
  children,
}: ResponsiveSegmentedControlProps): JSX.Element {
  return (
    <>
      <span className={styles.wide}>{children}</span>
      <span className={styles.narrow}>
        <SegmentedDropdown
          value={value}
          onChange={onChange}
          options={options}
        />
      </span>
    </>
  )
}
